import Guid from "_shared/guids/guid";
import { Masked } from "work/masked-fields";
import type WorkAgreement from "work/work-agreements/work-agreement";

/**
 * What a confirmation says taking a vendor team member off live work costs the fee schedule, or
 * nothing when it costs nothing (#6096).
 *
 * A rate a member held keeps their name, and their departure strikes it through; the next change
 * order has to restaff it before it can be hired. Somebody who stays on the work in another part --
 * leading either team, as an additional vendor, or on the client team -- has not left it, so no
 * departure is recorded and their rates stay as they are (`WorkAgreement.StaysOnTheWorkWithout`).
 * Starts with a space, to follow the sentence before it.
 */
export function ratesStruckThroughWarning(workAgreement: WorkAgreement, userId: Guid): string {
  const { feeSchedule } = workAgreement;
  if (Masked.isMasked(feeSchedule) || staysOnTheWorkWithoutTheVendorTeam(workAgreement, userId)) return "";
  const rates = feeSchedule
    .filter((category) => !category.teamMemberIsMasked && category.teamMember?.userId.isEqualTo(userId))
    .map((category) => category.name.value);
  if (rates.length === 0) return "";
  return ` These fee schedule rates will be struck through until a change order restaffs them: ${rates.join(", ")}.`;
}

function staysOnTheWorkWithoutTheVendorTeam(workAgreement: WorkAgreement, userId: Guid): boolean {
  const { vendorTeam, clientTeam } = workAgreement;
  const vendorLeader = Masked.isMasked(vendorTeam) ? undefined : vendorTeam?.leader;
  const client = Masked.isMasked(clientTeam) ? undefined : clientTeam;
  return [
    vendorLeader,
    client?.leader,
    ...workAgreement.additionalVendors,
    ...(client?.members ?? []),
  ].some((member) => member?.userId?.isEqualTo(userId));
}
